// Notification React Query Hooks

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { communicationService } from '../services/api/communicationService';
import {
  Notification,
  GetNotificationsRequest,
  GetNotificationsResponse,
  UnreadCountResponse,
  NotificationPreferences,
} from '../types/communication';
import toast from 'react-hot-toast';

// Query keys for notifications
export const NOTIFICATION_KEYS = {
  all: ['notifications'] as const,
  list: (params?: GetNotificationsRequest) => 
    [...NOTIFICATION_KEYS.all, 'list', params] as const,
  unreadCount: () => [...NOTIFICATION_KEYS.all, 'unread-count'] as const,
  preferences: () => [...NOTIFICATION_KEYS.all, 'preferences'] as const,
};

// ============================================
// NOTIFICATION QUERIES
// ============================================

export const useNotifications = (params?: GetNotificationsRequest) => {
  return useQuery<GetNotificationsResponse>({
    queryKey: NOTIFICATION_KEYS.list(params),
    queryFn: () => communicationService.getNotifications(params)
  });
};

export const useUnreadCount = () => {
  return useQuery<UnreadCountResponse>({
    queryKey: NOTIFICATION_KEYS.unreadCount(),
    queryFn: () => communicationService.getUnreadCount()
  });
};

export const useNotificationPreferences = () => {
  return useQuery<NotificationPreferences>({
    queryKey: NOTIFICATION_KEYS.preferences(),
    queryFn: () => communicationService.getNotificationPreferences()
  });
};

// ============================================
// NOTIFICATION MUTATIONS
// ============================================

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => 
      communicationService.markNotificationAsRead(notificationId),
    onSuccess: () => {
      // Invalidate notifications list and unread count
      queryClient.invalidateQueries({ 
        queryKey: NOTIFICATION_KEYS.list() 
      });
      queryClient.invalidateQueries({ 
        queryKey: NOTIFICATION_KEYS.unreadCount() 
      });
    },
    onError: (error: Error) => {
      console.error('Error marking notification as read:', error);
    },
  });
};

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => communicationService.markAllNotificationsAsRead(),
    onSuccess: (result) => {
      if (result.markedCount > 0) {
        toast.success(`${result.markedCount} notification(s) marked as read`);
      }
      queryClient.invalidateQueries({ 
        queryKey: NOTIFICATION_KEYS.list() 
      });
      queryClient.invalidateQueries({ 
        queryKey: NOTIFICATION_KEYS.unreadCount() 
      });
    },
    onError: (error: Error) => {
      console.error('Error marking all notifications as read:', error);
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => 
      communicationService.deleteNotification(notificationId),
    onSuccess: () => {
      toast.success('Notification deleted');
      queryClient.invalidateQueries({ 
        queryKey: NOTIFICATION_KEYS.list() 
      });
      queryClient.invalidateQueries({ 
        queryKey: NOTIFICATION_KEYS.unreadCount() 
      });
    },
    onError: (error: Error) => {
      console.error('Error deleting notification:', error);
    },
  });
};

export const useClearAllNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => communicationService.clearAllNotifications(),
    onSuccess: (result) => {
      if (result.clearedCount > 0) {
        toast.success(`${result.clearedCount} notification(s) cleared`);
      } else {
        toast.info('No read notifications to clear');
      }
      queryClient.invalidateQueries({ 
        queryKey: NOTIFICATION_KEYS.list() 
      });
      queryClient.invalidateQueries({ 
        queryKey: NOTIFICATION_KEYS.unreadCount() 
      });
    },
    onError: (error: Error) => {
      console.error('Error clearing notifications:', error);
    },
  });
};

export const useUpdatePreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<NotificationPreferences>) => 
      communicationService.updateNotificationPreferences(data),
    onSuccess: () => {
      toast.success('Notification preferences updated');
      queryClient.invalidateQueries({ 
        queryKey: NOTIFICATION_KEYS.preferences() 
      });
    },
    onError: (error: Error) => {
      console.error('Error updating preferences:', error);
    },
  });
};
